import React, { useMemo, useEffect } from 'react';
import { useSelector, getLocale, history, useIntl } from 'umi';
import ConsoleStoreRankTopFilter from '@/pages/intelligence/pages/StoreRanks/ConsoleRegion/components/ConsoleStoreRankTopFilter';
import StoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore';
import ExportTable from '@intelligence/components/ExportTable';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import SvgIcon from '@home/components/SvgIcon';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import useStoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore/_hooks/useStoreRankTopCore';
import { getValidGranularity } from '@/pages/intelligence/utils/commonUtils';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import {
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 组合国家筛选器
  COMPOSED_COUNTRY_SELECT as COMPOSED_COUNTRY_SELECT_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import '@/pages/intelligence/assets/scss/StoreRanks/PCGlobal/index.scss';
import './index.scss';
import { formatRegionOptions } from '@intelligence/components/RegionSelector';
import { getCountryCodeIntersection, isSameDate } from '@intelligence/utils/filterUtils';
import { CONSOLE_ENTITY_TYPE, NINTENDO_PLATFORM } from '@intelligence/v3/constants/topcharts';
import { getSortedTopsKeys } from '@/pages/intelligence/_config/storeRank/utils';

import type { IBaseProps, IDateType, IDateTypely } from '@/pages/intelligence/types/common';
import type { IStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';

const MORE_PAGE_PATH = '/intelligence/storeRanks/ConsoleRegion/more/nintendo';

const GAME_ID_KEY = 'app_id';
const BUSINESS_TYPE = 'console-nintendo-store-rank-top';
const DOWNLOAD_KEY = 'consoleStoreRankTop';
const DOWNLOAD_EXCEL_LOG_CFG_KEY = 'consoleNintendoStoreRankTopDownloadExcel';

// 榜单支持的时间粒度
const GRANULARITIES: IDateTypely[] = ['daily'];

// 榜单支持的时间粒度（RangeTime组件使用的）
const DATE_TYPES: IDateType[] = ['date'];

const PLATFORMS: IPlatform[] = [NINTENDO_PLATFORM];

// 默认筛选器数据，用于reset时、兜底
const DEFAULT_FILTER: IStoreRankTopFilter = {
  store: NINTENDO_PLATFORM,
  region_type: 'market',
  regions: ['us'],
  genre: [],
  sub_genre: [],
  genre_source: 'iegg',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
};

type IConsoleStoreRankTopNintendoProps = IBaseProps;

const ConsoleStoreRankTopNintendo: React.FC<IConsoleStoreRankTopNintendoProps> = (props) => {
  const {} = props;
  const { formatMessage } = useIntl();

  const { filterExtend, setFilterExtend } = useFilterExtend({});
  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]);
  // 是否操作过筛选器，判断是否继承的条件
  const isOperate = filterState?.isOperate || false;

  const { isLoading: regionsOptionsMapLoading, regionsOptions: regionOptions } = useStoreTopRegionOptionsMap(
    NINTENDO_PLATFORM,
    {
      entityType: CONSOLE_ENTITY_TYPE,
      platforms: PLATFORMS,
    },
  );

  const formatRegionParams = (markets: string[]) => {
    //保存的方案选择后可特殊处理国家值为交集之后的值
    const { countryCode } = formatRegionOptions(regionOptions);
    const result = getCountryCodeIntersection({
      markets: markets,
      countriesMarkets: countryCode,
      disableNoChina: true,
      defaultMarkets: ['us'],
    });
    return result;
  };
  // 初始化筛选器数据，合并继承值、默认值
  const initialFilter = useMemo<IStoreRankTopFilter>(() => {
    if (!isOperate) return DEFAULT_FILTER;

    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtend);
    const rangeTimeFilterExtend = filterExtendTmp?.[RANGE_TIME_SELECT_FILTER];
    const composedCountryFilterExtend = filterExtendTmp?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;

    const granularityTmp = rangeTimeFilterExtend?.granularity || DEFAULT_FILTER?.granularity;
    const initialGranularity = getValidGranularity(granularityTmp, GRANULARITIES);
    const initialDate =
      rangeTimeFilterExtend?.start_date ||
      rangeTimeFilterExtend?.end_date ||
      DEFAULT_FILTER.start_date;

    // 国家继承--nitendo作国家交集判断
    const initialRegions =
      composedCountryFilterExtend?.countries?.length === 1
        ? formatRegionParams(composedCountryFilterExtend?.countries)
        : DEFAULT_FILTER.regions;
    //如果有切换到默认值了，也把继承值跟着修改
    const newfilterExtend = {
      ...filterExtend,
      [COMPOSED_COUNTRY_SELECT_FILTER]: { value: { type: 'single', countries: initialRegions } },
    };
    setFilterExtend(newfilterExtend);
    return {
      ...DEFAULT_FILTER,
      granularity: initialGranularity,
      start_date: initialDate,
      end_date: initialDate,
      regions: initialRegions,
    };
  }, [isOperate, regionOptions]);

  const {
    // 榜单数据加载状态
    isLoading,
    // 榜单数据
    tops,
    // 筛选器数据
    filter,
    // 页面初始化
    onPageInit,
    // 筛选器confirm的回调
    onFilterConfirm,
    // 筛选器reset的回调
    onFilterReset,
    // 榜单卡片点击游戏的回调
    onCardGameClick,
    // 榜单卡片点击标题的回调
    onGoToStoreRankMore,
    getDetailGamePath,
  } = useStoreRankTopCore({
    entityType: CONSOLE_ENTITY_TYPE,
    // 游戏id的取值字段
    gameIdKey: GAME_ID_KEY,
    // 榜单卡片点击标题 跳转榜单下钻页的 路由path
    morePagePath: MORE_PAGE_PATH,
    // 页面打点配置的key
    pageLogCfgKey: 'consoleStoreRankTopNintendo',
    isOperate,
    // 初始化筛选器数据
    initialFilter,
    // 查询榜单数据 初始化的分页大小
    initialPageSize: 10,
  });

  useEffect(() => {
    if (regionsOptionsMapLoading) return;
    onPageInit(initialFilter);
  }, [regionsOptionsMapLoading]);

  const locale = getLocale();

  const downloadParams = useMemo(() => {
    const store = DEFAULT_FILTER.store;
    const granularity = filter?.granularity || DEFAULT_FILTER.granularity;
    return {
      entity_type: CONSOLE_ENTITY_TYPE,
      store,
      region_type: filter?.region_type || DEFAULT_FILTER.region_type,
      regions: filter?.regions || DEFAULT_FILTER.regions,
      genre: filter?.genre || [],
      sub_genre: filter?.sub_genre || [],
      genre_source: filter?.genre_source || DEFAULT_FILTER.genre_source,
      iegg_genre_req: filter?.iegg_genre_req || [],
      granularity,
      start_date: filter?.start_date || '',
      end_date: filter?.end_date || '',
      rankingSources:
        getSortedTopsKeys(CONSOLE_ENTITY_TYPE, store, granularity) || [],
    };
  }, [filter]);

  const downloadFileName = useMemo(() => {
    const startDate = filter?.start_date || '';
    const endDate = filter?.end_date || '';
    return `Store Ranks_Console_Nintendo_Top_${startDate}_${endDate}`;
  }, [filter?.start_date, filter?.end_date]);

  const handleClickMultiCountry = () => {
    const source = tops?.[0]?.name;
    if (!source) return;
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('source', encodeURIComponent(source));
    urlSearchParams.append('isCheckAllCountries', '1');
    history.push(`${MORE_PAGE_PATH}?${urlSearchParams.toString()}`);
  };

  const multiCountryText = (
    <span className="dbi-storerank-single-country-select-footer">
      {formatMessage({ id: 'dbi_store_rank_single_country_select_footer_btn_text' })}
      <SvgIcon type="aigc-jump" />
    </span>
  );

  return (
    <StoreRankTopCore
      className="dbi-nintendo dbi-console-store-rank-top dbi-console-store-rank-top-nintendo"
      entityType={CONSOLE_ENTITY_TYPE}
      showDownloadTips
      inlineDownload
      gameIdKey={GAME_ID_KEY}
      isLoading={isLoading}
      loadingContentStyle={{ height: 'calc(100vh - 472px)' }}
      tops={tops}
      filter={
        <ConsoleStoreRankTopFilter
          isOperate={isOperate}
          isMonthlyRank={false}
          isRegionVisible={true}
          isRegionDisabled={false}
          typeList={DATE_TYPES}
          granularity={filter?.granularity}
          startDate={filter?.start_date}
          endDate={filter?.end_date}
          multiCountryText={multiCountryText}
          onClickMultiCountry={handleClickMultiCountry}
          onConfirm={onFilterConfirm}
          onReset={onFilterReset}
          regionOptions={regionOptions}
        />
      }
      exportTable={
        <ExportTable
          entityType={CONSOLE_ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={DOWNLOAD_EXCEL_LOG_CFG_KEY}
          isDisableDownload={isLoading}
          total={tops?.length ?? 0}
          tableParams={downloadParams}
          newStyleFileName={downloadFileName}
          isShowMaxDownloadRowsTips={false}
          downloadModalSize={locale === LANGUAGE_CN ? 580 : 686}
          useDownloadAmountType={true}
          useWebDownload={true}
          downloadKey={DOWNLOAD_KEY}
          regionOptions={regionOptions}
        />
      }
      onCardTitleClick={onGoToStoreRankMore}
      onCardGameClick={onCardGameClick}
      getDetailGamePath={getDetailGamePath}
    />
  );
};

export default ConsoleStoreRankTopNintendo;
